import { Button, Col, Form, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";

export type AppCommentsListItem<TOption = unknown> = {
    id: number;
    value: string;
    option?: TOption;
};

export type AppCommentsListOption = {
    value: string;
    label: string;
};

type AppCommentsListProps<TOption = unknown> = {
    label?: string;
    placeholder: string;
    items: AppCommentsListItem<TOption>[];  
    getItemError?: (index: number) => string | undefined;
    onAdd: () => void;
    onRemove: (index: number) => void;
    onChange: (index: number, item: AppCommentsListItem<TOption>) => void;
    addAriaLabel: string;
    removeAriaLabel: string;
    options?: AppCommentsListOption[];
};

export default function AppCommentsList<TOption = unknown>({
    label,
    placeholder,
    items,
    getItemError,
    onAdd,
    onRemove,
    onChange,
    addAriaLabel,
    removeAriaLabel,
    options
}: AppCommentsListProps<TOption>) {
    const { t } = useTranslation(["common"]);
    const hasOptions = !!options?.length;

    return (
        <>
            {label &&
                <Row className="mt-4">
                    <Col xs={12}>
                        <h5 className="mb-0">{label}</h5>
                    </Col>
                </Row>
            }
            {items.map((item, index) => {
                const isLastItem = index === items.length - 1;
                const itemError = getItemError?.(index);
                return (
                    <>
                    <Row className="mt-2" key={`comment-${item.id}-${index}`}>
                        <Col xs={hasOptions ? 8 : 10} md={hasOptions ? 8 : 10}>
                            <Form.Control
                                type="text"
                                value={item.value ?? ""}
                                placeholder={placeholder}
                                isInvalid={!!itemError}
                                onChange={(e) => onChange(index, { ...item, value: e.target.value })}
                            />
                            <Form.Control.Feedback type="invalid">
                                {itemError}
                            </Form.Control.Feedback>
                        </Col>
                        {hasOptions && (
                            <Col xs={2} md={2}>
                                <Form.Select
                                    value={item.option as any}
                                    onChange={(e) => {
                                        const selected = options?.find(
                                            o => String(o.value) === e.target.value
                                        );
                                        onChange(index, {
                                            ...item,
                                            option: selected?.value as TOption
                                        });
                                    }}
                                >
                                    <option value="">{t("common:selectDefaultText") }</option>
                                    {options?.map((o) => (
                                        <option key={String(o.value)} value={String(o.value)}>
                                            {o.label}
                                        </option>
                                    ))}
                                </Form.Select>
                            </Col>
                        )}
                            <Col xs={2} md={2} className="text-end ">
                                <Button onClick={() => onRemove(index)} variant="secondary" aria-label={removeAriaLabel}>
                                    <i className="bi bi-x"></i>
                            </Button>

                        </Col>
                    </Row>
                     <Row>
                         <Col className="text-end mt-2">
                            {isLastItem && (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    onClick={onAdd}
                                    aria-label={addAriaLabel}
                                 >
                                     {addAriaLabel}

                                </Button>
                            )}
                        </Col>
                    </Row>
                    </>
                );
            })}
            {items.length === 0 && (
                <Row className="mt-2">
                    <Col xs={12} className="text-end me-4">
                        <Button
                            type="button"
                            variant="secondary"
                            onClick={onAdd}
                            aria-label={addAriaLabel}
                        >
                            {addAriaLabel}
                        </Button>
                    </Col>
                </Row>
            )}
        </>
    );
}
